// Servers page — add / edit side panel (slides in from the right; the list stays visible behind it).
// Compact: every section fits on one screen without scrolling; long hints are hover tooltips.
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { SvIcon } from './svIcons';

function PassInput({ value, onChange, placeholder, required }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <div className="sv-pass">
      <input className="sv-input" type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} required={required} autoComplete="new-password" />
      <button type="button" className="sv-icon-btn" onClick={() => setShow(s => !s)}
        title={show ? t('serversModal.hidePassword', 'Hide password') : t('serversModal.showPassword', 'Show password')}>
        <SvIcon name={show ? 'eyeOff' : 'eye'} size={14} />
      </button>
    </div>
  );
}

function Section({ icon, tone, title, hint, action, children }) {
  return (
    <section className="sv-section">
      <div className="sv-section-head" style={{ '--sv-tone': tone }}>
        <span className="ic"><SvIcon name={icon} size={12} /></span>
        <div><b>{title}</b>{hint && <small title={hint}>{hint}</small>}</div>
        {action}
      </div>
      {children}
    </section>
  );
}

const Label = ({ children, req, tip }) => (
  <label title={tip || undefined}>{children}{req && <span className="req">*</span>}</label>
);

export default function ServerPanel({
  open, editing, formData, setFormData, onClose, onSubmit,
  onTestRcon, onTestBackup, onUploadConfig, testingConn, backupTestSuccess, uploadingConfig,
}) {
  const { t } = useTranslation();
  const [pathHelp, setPathHelp] = useState(false);
  const set = (patch) => setFormData(f => ({ ...f, ...patch }));
  const field = (key) => ({ value: formData[key] ?? '', onChange: e => set({ [key]: e.target.value }) });
  const isLanEdit = typeof formData.ip === 'string' && formData.ip.startsWith('lan.');
  const showLgsm = formData.backup_protocol === 'sftp' && (formData.backup_host || formData.ip) && formData.backup_user;

  useEffect(() => {
    if (!open) return;
    setPathHelp(false);
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, onClose]);

  const running = (key, idle, label) => (testingConn === key ? <><span className="sv-spin" />{label}</> : idle);

  return (
    <AnimatePresence>
      {open && (<>
        <motion.div className="sv-panel-backdrop" onClick={onClose}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} />
        <motion.aside className="sv-panel" role="dialog" aria-modal="true"
          initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.24, ease: [0.16, 1, 0.3, 1] }}>
          <div className="sv-panel-head">
            <span className="sv-panel-icon"><SvIcon name="server" size={16} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3>{editing ? t('serversModal.editTitle', 'Edit Server') : t('serversModal.addTitle', 'Add New Server')}</h3>
              <p>{editing ? editing.name : t('serversModal.subtitle', 'Configure RCON access and optional backup')}</p>
            </div>
            <button type="button" className="sv-icon-btn" onClick={onClose} title={t('common.close', 'Close')}><SvIcon name="close" /></button>
          </div>

          <form onSubmit={onSubmit} style={{ display: 'contents' }}>
            <div className="sv-panel-body">
              {/* Basics + RCON */}
              <Section icon="server" tone="#3b82f6" title={t('serversModal.serverInfo', 'Server Info')} hint={t('serversModal.serverInfoHint', 'RCON connection - required')}>
                {isLanEdit ? (<>
                  <div className="sv-field">
                    <Label req>{t('serversModal.serverName', 'Server Name')}</Label>
                    <input className="sv-input" type="text" required placeholder={t('serversModal.serverNamePlaceholder', 'e.g. Match Server 1')} {...field('name')} autoFocus />
                  </div>
                  <div className="sv-note">{t('lan.editNote', 'LAN server: its address, port and RCON password are managed by the venue agent (Servers → LAN Environments).')}</div>
                </>) : (<>
                  <div className="sv-fields basics">
                    <div className="sv-field">
                      <Label req>{t('serversModal.serverName', 'Server Name')}</Label>
                      <input className="sv-input" type="text" required placeholder={t('serversModal.serverNamePlaceholder', 'e.g. Match Server 1')} {...field('name')} autoFocus />
                    </div>
                    <div className="sv-field">
                      <Label req>{t('serversModal.ipAddress', 'IP Address')}</Label>
                      <input className="sv-input mono" type="text" required placeholder="192.168.1.100" {...field('ip')} />
                    </div>
                    <div className="sv-field">
                      <Label req>{t('serversModal.port', 'Port')}</Label>
                      <input className="sv-input mono" type="number" required placeholder="27015" {...field('port')} />
                    </div>
                  </div>
                  <div className="sv-inline" style={{ alignItems: 'flex-end' }}>
                    <div className="sv-field">
                      <Label req={!editing}>{t('serversModal.rconPassword', 'RCON Password')}</Label>
                      <PassInput {...field('rcon_password')} required={!editing} placeholder={editing ? t('serversModal.keepCurrent', 'Keep current') : ''} />
                    </div>
                    <button type="button" className="sv-btn small" style={{ height: 31 }} onClick={onTestRcon} disabled={testingConn === 'modal-rcon'}>
                      {running('modal-rcon', <><SvIcon name="zap" size={13} />{t('serversModal.testRcon', 'Test RCON')}</>, t('serversModal.testingRcon', 'Checking RCON…'))}
                    </button>
                  </div>
                </>)}
              </Section>

              {/* Display address (LAN / public IP) */}
              <Section icon="globe" tone="#22c55e" title={t('serversModal.lanPublicIp', 'LAN / Public IP')} hint={t('serversModal.lanPublicIpHint', 'Optional, display-only')}>
                <div className="sv-field">
                  <Label>{t('serversModal.publicIp', 'Public IP')}</Label>
                  <input className="sv-input mono" type="text" placeholder={t('servers.publicIpShort', 'Empty = main IP')} {...field('public_ip')} />
                  <span className="hint">{t('serversModal.publicIpHint', 'shown on the admin match page and post-veto connect screen; main IP is still used for everything else')}</span>
                </div>
              </Section>

              {/* Valve SDR relay address after the veto */}
              <Section icon="link" tone="#06b6d4" title={t('servers.sdrTitle', 'SDR relay')} hint={t('servers.sdrSubtitle', 'Optional, post-veto connect screen')}>
                <label className={`sv-toggle${formData.show_sdr_after_veto ? ' on' : ''}`}>
                  <input type="checkbox" className="sv-check" checked={!!formData.show_sdr_after_veto} onChange={e => set({ show_sdr_after_veto: e.target.checked })} />
                  <b>{t('serversModal.showSdrAfterVeto', 'Show SDR IP after veto')}</b>
                </label>
                <span className="sv-field"><span className="hint">
                  {t('serversModal.showSdrAfterVetoHint', 'When on, the post-veto connect screen also shows the Valve SDR relay address to team captains and admins - a fallback for players with bad routing, ping spikes, or from another region. Never shown to public viewers.')}
                </span></span>
              </Section>

              {/* SFTP / FTP - not for agent-only LAN servers (no file channel) */}
              {!isLanEdit && (
                <Section icon="drive" tone="#a78bfa" title={t('serversModal.sftpFtpInfo', 'SFTP / FTP Info')} hint={t('serversModal.sftpFtpHint', 'Optional - enables demo backups & config upload')}
                  action={(
                    <div className="sv-section-actions">
                      {editing && backupTestSuccess && (
                        <button type="button" className="sv-btn small success" onClick={onUploadConfig} disabled={uploadingConfig === editing.id}>
                          <SvIcon name="upload" size={12} />
                          {uploadingConfig === editing.id ? t('serversModal.uploading', 'Uploading...') : t('servers.uploadConfig', 'Upload configs')}
                        </button>
                      )}
                      <button type="button" className="sv-btn small" onClick={onTestBackup} disabled={testingConn === 'modal-backup'}>
                        {running('modal-backup', <><SvIcon name="link" size={12} />{t('serversModal.testConnection', 'Test Connection')}</>, t('serversModal.testingConnection', 'Checking connection…'))}
                      </button>
                    </div>
                  )}>
                  <div className="sv-fields three">
                    <div className="sv-field">
                      <Label>{t('serversModal.protocol', 'Protocol')}</Label>
                      <div className="sv-seg">
                        {[['sftp', 'SFTP'], ['ftp', 'FTP']].map(([v, l]) => (
                          <button key={v} type="button" style={{ flex: 1 }} className={formData.backup_protocol === v ? 'on' : ''} onClick={() => set({ backup_protocol: v })}>{l}</button>
                        ))}
                      </div>
                    </div>
                    <div className="sv-field">
                      <Label>{t('serversModal.host', 'Host')}</Label>
                      <input className="sv-input mono" type="text" placeholder={t('servers.hostDefault', 'Defaults to the IP')} {...field('backup_host')} />
                    </div>
                    <div className="sv-field">
                      <Label>{t('serversModal.port', 'Port')}</Label>
                      <input className="sv-input mono" type="number" placeholder={formData.backup_protocol === 'ftp' ? '21' : '22'} {...field('backup_port')} />
                    </div>
                    <div className="sv-field">
                      <Label>{t('serversModal.username', 'Username')}</Label>
                      <input className="sv-input" type="text" autoComplete="off" {...field('backup_user')} />
                    </div>
                    <div className="sv-field">
                      <Label>{t('serversModal.password', 'Password')}</Label>
                      <PassInput {...field('backup_password')} placeholder={editing ? t('serversModal.keepCurrent', 'Keep current') : ''} />
                    </div>
                    <div className="sv-field">
                      <label>
                        {t('serversModal.remotePath', 'Remote Path')}
                        <button type="button" className="sv-help" onClick={() => setPathHelp(h => !h)} title={t('servers.pathExamples', 'Path examples')}><SvIcon name="help" size={13} /></button>
                      </label>
                      <input className="sv-input mono" type="text" placeholder="/csgo" {...field('backup_remote_path')} />
                    </div>
                  </div>
                  {pathHelp && (
                    <div className="sv-pathhelp">
                      {t('servers.pathHelpIntro', 'Point it at the CS2 server\'s csgo folder:')}{' '}
                      Pterodactyl <code>/game/csgo</code>, DatHost <code>/</code>, SteamCMD / LinuxGSM <code>/home/user/server/game/csgo</code>.{' '}
                      {t('servers.pathHelpTest', 'Use Test Connection to check it.')}
                    </div>
                  )}
                </Section>
              )}

              {/* LinuxGSM - only over SFTP */}
              {showLgsm && (
                <Section icon="wrench" tone="#f59e0b" title={t('serversModal.linuxGsmTitle', 'LinuxGSM SSH Management')} hint={t('serversModal.linuxGsmHintFull', 'Start, stop, restart, update, validate, monitor, backup and a live console over SSH')}>
                  <div className="sv-inline">
                    <label className={`sv-toggle${formData.linuxgsm_enabled ? ' on' : ''}`} style={{ flexShrink: 0 }}>
                      <input type="checkbox" className="sv-check" checked={!!formData.linuxgsm_enabled} onChange={e => set({ linuxgsm_enabled: e.target.checked })} />
                      <b>{t('serversModal.enableLinuxGsm', 'Enable LinuxGSM')}</b>
                    </label>
                    {formData.linuxgsm_enabled && (
                      <input className="sv-input mono" type="text" placeholder="cs2server" title={t('servers.lgsmScriptPath', 'Script name in the SSH user\'s home (cs2server), or its full path (/home/cs2server/cs2server)')} style={{ maxWidth: 260 }} {...field('linuxgsm_script')} />
                    )}
                  </div>
                </Section>
              )}
            </div>

            <div className="sv-panel-foot">
              <button type="button" className="sv-btn" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
              <button type="submit" className="sv-btn primary">{t('serversModal.saveServer', 'Save Server')}</button>
            </div>
          </form>
        </motion.aside>
      </>)}
    </AnimatePresence>
  );
}
